import React, { useState } from 'react';
import moment from 'moment';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowDown01Icon,
  ArrowUpRight01Icon,
  SplitIcon,
  Calendar03Icon,
  Edit02Icon,
  FlashIcon,
  Link01Icon,
  Mail01Icon,
  Message01Icon,
  RefreshIcon,
  SquareLock02Icon,
  Tick02Icon,
  Ticket01Icon,
  ArrowDataTransferHorizontalIcon,
  UserCheck01Icon,
  WorkHistoryIcon
} from '@hugeicons/core-free-icons';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { getBookingModeTag } from '../../../../../shared/services/helpers/stay-type.helper';
import { BookOfferResponseModel } from '../../../../../shared/ui-models/book.ui-model';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import {
  hasAccess,
  hasRole
} from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { getBookOffer, reservationSwitchRoom } from '../../../../shared/services/data.service';
import { SwitchRoomModal } from '../switch-room-modal/switch-room-modal.component';
import { RaiseTicketModal } from '../../../../../shared/components/group-ui/raise-ticket/raise-ticket-modal.component';
import {
  ReservationSourceChannelModal,
  SourceChannelDraft
} from './reservation-source-channel-modal.component';
import { SAHEL_AREAS, dropdownItemStatus } from '../../reservation-edit.helper';
import { updateOneReservations } from '../../../../shared/services/data.service';
import Tooltip from '../../../../../ui/Tooltip';
import Popover from '../../../../../ui/Popover';
import {
  getReservationChannelImage,
  getReservationChannelLabel,
  getReservationSourceImage,
  getReservationSourceLabel,
  resolveAttributionChannels
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import {
  getAdTypeLabel,
  getMarketingChannelLabel
} from '../../../../../shared/interfaces/attribution.interface';

export interface ReservationHeaderProps {
  data: {
    reservation: ReservationDetailsUiModel;
    reservationStatus: string | undefined;
    bookofferLoading: boolean;
    canChangeDates: boolean;
    showSwitchRoomModal: boolean;
  };
  eventHandlers: {
    onStatusChange: (newStatus: string) => void;
    onOpenAddCommentModal: () => void;
    onReactivateModalOpen: (bookoffer: BookOfferResponseModel) => void;
    onBookofferLoadingChange: (loading: boolean) => void;
    onRefresh: (id: string) => Promise<any>;
    onOpenSplitReservationModal: () => void;
    onOpenChangeDatesModal: () => void;
    onOpenResendEmailModal: () => void;
    onOpenCommentsModal: () => void;
    onOpenHistoryModal: () => void;
    onSwitchRoomModalChange: (open: boolean) => void;
  };
}

interface ActionItem {
  key: string;
  label: string;
  icon: typeof FlashIcon;
  onClick: () => void;
  disabledReason?: string;
}

interface ActionGroup {
  title: string;
  items: ActionItem[];
}

// Stable references: ui/Popover recomputes its position whenever these change identity.
const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' } as const;
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' } as const;

const STATUS_STYLES: Record<string, { pill: string; dot: string }> = {
  [ReservationStatus.CONFIRMATION_PENDING]: {
    pill: 'bg-action-alert/20 border-action-alert/40 text-neutral-500',
    dot: 'bg-action-alert'
  },
  [ReservationStatus.PENDING_CHECK_IN]: {
    pill: 'bg-blue/60 border-blue text-neutral-500',
    dot: 'bg-neutral-500'
  },
  [ReservationStatus.CHECKED_IN_STARTED]: {
    pill: 'bg-secondary-50 border-secondary-100 text-secondary-700',
    dot: 'bg-secondary-500'
  },
  [ReservationStatus.CHECKED_IN]: {
    pill: 'bg-primary-600 border-primary-600 text-neutral-0',
    dot: 'bg-primary-100'
  },
  [ReservationStatus.IN_HOUSE]: {
    pill: 'bg-primary-600 border-primary-600 text-neutral-0',
    dot: 'bg-primary-100'
  },
  [ReservationStatus.CHECKED_OUT]: {
    pill: 'bg-primary-50 border-primary-100 text-primary-700',
    dot: 'bg-primary-500'
  },
  [ReservationStatus.CANCELED]: {
    pill: 'bg-scarlet/10 border-scarlet/20 text-scarlet',
    dot: 'bg-scarlet'
  },
  [ReservationStatus.EXPIRED]: {
    pill: 'bg-gray-200 border-gray-200 text-neutral-300',
    dot: 'bg-neutral-100'
  }
};

const DEFAULT_STATUS_STYLE = {
  pill: 'bg-creams-100 border-gray-200 text-neutral-500',
  dot: 'bg-neutral-300'
};

const getStatusStyle = (status?: string) =>
  (status && STATUS_STYLES[status]) || DEFAULT_STATUS_STYLE;

const iconButtonClassName =
  'relative size-[44px] rounded-full grid place-items-center text-neutral-500 hover:bg-creams-200 transition-colors cursor-pointer';

const chipClassName =
  'inline-flex items-center gap-[4px] px-[12px] py-[4px] rounded-full text-[13px] font-gilroy-medium whitespace-nowrap';

const menuItemClassName =
  'w-full flex items-center gap-[10px] px-[14px] py-[10px] rounded-[12px] text-left text-[15px] text-neutral-500 hover:bg-creams-100 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:text-neutral-100 disabled:hover:bg-transparent';

export const ReservationHeader = ({
  data: { reservation, reservationStatus, bookofferLoading, canChangeDates, showSwitchRoomModal },
  eventHandlers: {
    onStatusChange,
    onOpenAddCommentModal,
    onReactivateModalOpen,
    onBookofferLoadingChange,
    onRefresh,
    onOpenSplitReservationModal,
    onOpenChangeDatesModal,
    onOpenResendEmailModal,
    onOpenCommentsModal,
    onOpenHistoryModal,
    onSwitchRoomModalChange
  }
}: ReservationHeaderProps) => {
  const canReopenCanceledReservation = hasRole([
    UserRole.RESERVATION_MANAGER,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  const statusOptions =
    reservation.status === ReservationStatus.CANCELED &&
    canReopenCanceledReservation &&
    reservation.source !== ReservationSource.CHANNEL_MANAGER
      ? [
          ...dropdownItemStatus[reservation.status],
          { label: 'Check in', value: ReservationStatus.CHECKED_IN }
        ]
      : dropdownItemStatus[reservation.status];

  const bookingModeTag = getBookingModeTag(
    reservation.bookingMode,
    reservation.checkInDate,
    reservation.checkOutDate
  );

  const isChannelManager = reservation.source === ReservationSource.CHANNEL_MANAGER;
  const channelManagerLockReason = `Updates only on ${
    reservation.channel ? reservation.channel.toUpperCase() : 'OTA directly'
  }`;

  const needsSahelValidation =
    !!reservation.areaId &&
    SAHEL_AREAS?.includes(reservation.areaId) &&
    (reservation.status === ReservationStatus.CHECKED_IN ||
      reservation.status === ReservationStatus.PENDING_CHECK_IN);

  const [switchRoomLoading, setSwitchRoomLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [showSourceChannelModal, setShowSourceChannelModal] = useState(false);
  const [sourceChannelDraft, setSourceChannelDraft] = useState<SourceChannelDraft>({});
  const [sourceChannelSaving, setSourceChannelSaving] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  const [showRaiseTicket, setShowRaiseTicket] = useState(false);

  const canEditSourceChannel = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess
  ]);
  // Mirrors the permission admin-connect enforces on POST /reservations/:id/resend-email.
  const canResendGuestEmail = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess
  ]);
  const canChangeStatus =
    !isChannelManager &&
    hasAccess([PERMISSIONS_LIST.reservationsFullAccess, PERMISSIONS_LIST.reservationsModifyAccess]);
  const statusLockReason = isChannelManager ? channelManagerLockReason : 'No permission to change';

  const saveAttribution = async (changes: SourceChannelDraft) => {
    const {
      source,
      channel,
      salesPerson,
      typeOfStay,
      brokerId,
      companyId,
      marketingChannel,
      messagingChannel
    } = changes;

    setSourceChannelSaving(true);
    const response = await updateOneReservations({
      id: reservation.id,
      source,
      channel,
      salesPerson,
      typeOfStay,
      // Only the broker id travels; server.service.property derives `brokerName`
      // from it and ignores any name sent alongside, so the two can never
      // disagree. Moving the segment off BROKER clears both server-side -- an
      // omitted field cannot express an unset over JSON.
      brokerId,
      // Same as the broker: only the id travels, and server.service.property
      // copies the company's contact person onto the reservation from it.
      // Moving the segment off CORPORATES clears both server-side.
      companyId,
      // Same rule as the create form: a marketing or messaging answer picked
      // while the source was CX must not survive a switch to a source that
      // could never have produced it.
      ...resolveAttributionChannels(source, { marketingChannel, messagingChannel })
    });
    setSourceChannelSaving(false);

    if (isErrorInfo(response)) {
      birdToast.error('Failed to update reservation attribution');
      return false;
    }

    await onRefresh(reservation.id);
    birdToast.success('Reservation attribution updated');
    return true;
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const handleSwitchRoom = (unitTypeId: number, roomId: string) => {
    setSwitchRoomLoading(true);
    reservationSwitchRoom(
      Number(reservation?.id || 0),
      Number(reservation?.unitTypeId || 0),
      unitTypeId,
      roomId
    ).then((response) => {
      setSwitchRoomLoading(false);
      if (!isErrorInfo(response)) {
        birdToast.success('Reservation room switched, and an email is sent to Property Manager(s)');
        onRefresh(String(reservation?.id)).then(() => onSwitchRoomModalChange(false));
      } else {
        if (response.status === 409) {
          birdToast.error(
            'Switch room operation failed, as destination room is not available at the same dates, please contact support for taking further actions'
          );
        } else {
          birdToast.error(response.message || 'Switch room operation failed, Please contact support.');
        }
      }
    });
  };

  const handleReactivate = () => {
    onBookofferLoadingChange(true);
    getBookOffer(
      reservation.unitTypeId || '',
      moment(reservation.checkInDate).format('DD/MM/YYYY'),
      moment(reservation.checkOutDate).format('DD/MM/YYYY')
    ).then((res) => {
      onBookofferLoadingChange(false);
      if (
        !isErrorInfo(res) &&
        (res as BookOfferResponseModel).availableRooms &&
        (res as BookOfferResponseModel).availableRooms.length > 0
      ) {
        onReactivateModalOpen(res as BookOfferResponseModel);
      } else {
        birdToast.error('No available rooms to reactivate reservation');
      }
    });
  };

  const handleToggleValidation = () => {
    updateOneReservations({
      id: reservation.id,
      isValidated: !reservation.isValidated
    }).then(() => onRefresh(reservation.id));
  };

  const openSourceChannelModal = () => {
    setSourceChannelDraft({
      source: reservation.source as ReservationSource | undefined,
      channel: reservation.channel,
      salesPerson: reservation.salesPerson,
      typeOfStay: reservation.typeOfStay,
      brokerId: reservation.brokerId,
      companyId: reservation.companyId,
      marketingChannel: reservation.marketingChannel,
      messagingChannel: reservation.messagingChannel
    });
    setShowSourceChannelModal(true);
  };

  // Every reservation-level action lives in this one menu. Each item keeps the
  // exact permission / state check the standalone button it replaced had.
  const actionGroups: ActionGroup[] = [
    {
      title: 'Stay',
      items: [
        {
          key: 'switch-room',
          label: reservation.roomName ? 'Switch room' : 'Assign room',
          icon: ArrowDataTransferHorizontalIcon,
          onClick: () => onSwitchRoomModalChange(true)
        },
        ...(canChangeDates
          ? [
              {
                key: 'change-dates',
                label: 'Change dates',
                icon: Calendar03Icon,
                onClick: onOpenChangeDatesModal,
                disabledReason: isChannelManager
                  ? channelManagerLockReason
                  : !reservation.source
                  ? 'Source not set'
                  : undefined
              }
            ]
          : []),
        ...(canReopenCanceledReservation
          ? [
              {
                key: 'shorten',
                label: 'Shorten reservation',
                icon: SplitIcon,
                onClick: onOpenSplitReservationModal
              }
            ]
          : [])
      ]
    },
    {
      title: 'Guest',
      items: [
        ...(canResendGuestEmail
          ? [
              {
                key: 'resend-email',
                label: 'Resend guest email',
                icon: Mail01Icon,
                onClick: onOpenResendEmailModal
              }
            ]
          : []),
        ...(needsSahelValidation
          ? [
              {
                key: 'validation',
                label: reservation.isValidated ? 'Mark as not validated' : 'Mark as validated',
                icon: UserCheck01Icon,
                onClick: handleToggleValidation
              }
            ]
          : [])
      ]
    },
    {
      title: 'Booking',
      items: [
        ...(canEditSourceChannel && (reservation.channel || reservation.source)
          ? [
              {
                key: 'attribution',
                label: 'Edit source & attribution',
                icon: Edit02Icon,
                onClick: openSourceChannelModal
              }
            ]
          : []),
        {
          key: 'reactivate',
          label: 'Reactivate',
          icon: RefreshIcon,
          onClick: handleReactivate,
          disabledReason:
            reservation.status !== ReservationStatus.EXPIRED ? 'Expired only' : undefined
        }
      ]
    },
    {
      title: 'Support',
      items: hasAccess([PERMISSIONS_LIST.ticketsFullAccess, PERMISSIONS_LIST.ticketsCreateAccess])
        ? [
            {
              key: 'raise-ticket',
              label: 'Raise ticket',
              icon: Ticket01Icon,
              onClick: () => setShowRaiseTicket(true)
            }
          ]
        : []
    }
  ].filter((group) => group.items.length > 0);

  const closeActionsMenu = () => setActionsAnchorEl(null);
  const closeStatusMenu = () => setStatusAnchorEl(null);

  const currentStatusLabel =
    statusOptions?.find((option) => option.value === reservationStatus)?.label ||
    statusOptions?.[0]?.label ||
    reservation.status;
  const statusStyle = getStatusStyle(reservationStatus || reservation.status);
  const statusPillClassName = `h-[44px] inline-flex items-center gap-[10px] pl-[16px] pr-[14px] rounded-[12px] border text-[14px] font-gilroy-medium whitespace-nowrap ${statusStyle.pill}`;

  const renderAttributionTooltip = () => (
    <span className="text-lg">
      {getReservationSourceLabel(reservation.source)} →{' '}
      {getReservationChannelLabel(reservation.channel)}
      <br />
      Sales person: {reservation.salesPerson || 'Unassigned'}
      {/*
        Only shown when there is one. A blank "Broker: —" on the
        overwhelming majority of bookings that came through no
        broker would be noise in a tooltip this small.
      */}
      {reservation.brokerName && (
        <>
          <br />
          Broker: {reservation.brokerName}
        </>
      )}
      {/*
        The marketing side of the same question. Kept in this
        tooltip rather than given its own surface: this is
        already where someone looks to ask where a reservation
        came from, and the two answers belong together.
      */}
      {(reservation.marketingChannel || reservation.utm_campaign) && (
        <>
          <br />
          Marketing: {getMarketingChannelLabel(reservation.marketingChannel)} (
          {getAdTypeLabel(reservation.adType)})
          {reservation.utm_campaign && (
            <>
              <br />
              Campaign: {reservation.utm_campaign}
            </>
          )}
        </>
      )}
    </span>
  );

  const statusDot = <span className={`size-[8px] rounded-full shrink-0 ${statusStyle.dot}`} />;

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-[16px]">
        <div className="min-w-0">
          <div className="flex items-center gap-[10px] flex-wrap">
            <h1 className="m-0 text-[clamp(26px,2.6vw,40px)] leading-tight font-gilroy-bold text-neutral-500">
              Reservation #{reservation.id}
            </h1>
            <Tooltip title={urlCopied ? 'Copied!' : 'Copy link'} placement="top">
              <button
                type="button"
                onClick={handleCopyUrl}
                className="size-[36px] rounded-full grid place-items-center text-neutral-300 hover:bg-creams-200 hover:text-neutral-500 transition-colors cursor-pointer"
                aria-label="Copy page URL"
              >
                <HugeiconsIcon icon={urlCopied ? Tick02Icon : Link01Icon} size={20} />
              </button>
            </Tooltip>
          </div>

          <div className="mt-[8px] flex items-center gap-[6px] flex-wrap text-[15px] text-neutral-500">
            {reservation.propertyName && (
              <a
                href={`${process.env.PUBLIC_URL}/properties/edit/${reservation.propertyId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-500 underline underline-offset-[3px] font-gilroy-medium hover:text-neutral-500"
              >
                {reservation.propertyName}
              </a>
            )}
            {reservation.unitName && (
              <>
                <span className="text-neutral-300">·</span>
                <a
                  href={`${process.env.PUBLIC_URL}/unit-types/edit/${reservation.unitTypeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-500 no-underline hover:underline hover:text-neutral-500 underline-offset-[3px]"
                >
                  {reservation.unitName}
                </a>
              </>
            )}
            <span className="text-neutral-300">·</span>
            {reservation.roomName ? (
              <span>
                <span className="text-neutral-300">Room </span>
                <span className="font-gilroy-medium">{reservation.roomName}</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onSwitchRoomModalChange(true)}
                className="px-[10px] py-[2px] rounded-full bg-secondary-50 text-secondary-700 text-[13px] font-gilroy-medium cursor-pointer"
              >
                No room assigned
              </button>
            )}
            {reservation.area && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="text-neutral-300">{reservation.area}</span>
              </>
            )}
          </div>

          <div className="mt-[12px] flex items-center gap-[8px] flex-wrap">
            {bookingModeTag && (
              <Tooltip title={bookingModeTag.tooltip}>
                <span
                  className={`${chipClassName} ${
                    bookingModeTag.isInstant
                      ? 'bg-primary-50 text-primary-700'
                      : 'bg-creams-100 border border-gray-200 text-neutral-500'
                  }`}
                >
                  {bookingModeTag.isInstant && <HugeiconsIcon icon={FlashIcon} size={14} />}
                  {bookingModeTag.label}
                </span>
              </Tooltip>
            )}
            {(reservation.channel || reservation.source) && (
              <Tooltip title={renderAttributionTooltip()} placement="top">
                <span
                  className={`${chipClassName} gap-[6px] bg-creams-100 border border-gray-200 text-neutral-500 cursor-default`}
                >
                  <img
                    className="size-[16px] object-contain"
                    src={getReservationSourceImage(reservation.source)}
                    alt={getReservationSourceLabel(reservation.source)}
                  />
                  {getReservationSourceLabel(reservation.source)} →{' '}
                  <img
                    className="size-[16px] object-contain"
                    src={getReservationChannelImage(reservation.channel)}
                    alt={getReservationChannelLabel(reservation.channel)}
                  />
                  {getReservationChannelLabel(reservation.channel)}
                </span>
              </Tooltip>
            )}
            {reservation?.relatedReservationId && (
              <Tooltip title="Open related reservation" placement="top">
                <a
                  href={`${process.env.PUBLIC_URL}/reservations/edit/${reservation.relatedReservationId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${chipClassName} bg-blue/40 text-neutral-500 no-underline hover:no-underline hover:bg-blue/70 hover:text-neutral-500`}
                >
                  Linked #{reservation.relatedReservationId}
                  <HugeiconsIcon icon={ArrowUpRight01Icon} size={14} />
                </a>
              </Tooltip>
            )}
            {reservation?.isComplimentary && (
              <span className={`${chipClassName} bg-primary-50 text-primary-700 cursor-default`}>
                Complimentary
              </span>
            )}
            {needsSahelValidation && !reservation.isValidated && (
              <Tooltip title="Mark as validated from the Actions menu">
                <span
                  className={`${chipClassName} bg-secondary-50 text-secondary-700 cursor-default`}
                >
                  Not validated
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="flex items-center gap-[6px] flex-wrap shrink-0">
          {reservation.status === ReservationStatus.EXPIRED && (
            <button
              type="button"
              onClick={handleReactivate}
              disabled={bookofferLoading}
              className="h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-scarlet text-neutral-0 text-[14px] font-gilroy-medium hover:brightness-110 disabled:opacity-60 cursor-pointer"
            >
              <HugeiconsIcon icon={RefreshIcon} size={16} />
              {bookofferLoading ? 'Checking…' : 'Reactivate'}
            </button>
          )}

          <Tooltip title="Comments" placement="top">
            <button
              type="button"
              onClick={onOpenCommentsModal}
              className={iconButtonClassName}
              aria-label="Open comments"
            >
              <HugeiconsIcon icon={Message01Icon} size={22} />
              {reservation.messages && reservation.messages.length > 0 && (
                <span className="absolute top-[2px] right-[2px] min-w-[20px] h-[20px] px-[4px] grid place-items-center rounded-full bg-primary-600 text-neutral-0 text-[10px] font-gilroy-bold leading-none">
                  {reservation.messages.length}
                </span>
              )}
            </button>
          </Tooltip>

          <Tooltip title="History" placement="top">
            <button
              type="button"
              onClick={onOpenHistoryModal}
              className={iconButtonClassName}
              aria-label="Open history"
            >
              <HugeiconsIcon icon={WorkHistoryIcon} size={22} />
            </button>
          </Tooltip>

          <button
            type="button"
            onClick={(event) => setActionsAnchorEl(event.currentTarget)}
            className="h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] border border-gray-200 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:border-neutral-500 transition-colors cursor-pointer whitespace-nowrap"
            aria-haspopup="menu"
            aria-expanded={Boolean(actionsAnchorEl)}
          >
            Actions
            <HugeiconsIcon icon={ArrowDown01Icon} size={16} />
          </button>

          {canChangeStatus ? (
            <button
              type="button"
              onClick={(event) => setStatusAnchorEl(event.currentTarget)}
              className={`${statusPillClassName} hover:brightness-95 cursor-pointer`}
              aria-haspopup="menu"
              aria-expanded={Boolean(statusAnchorEl)}
            >
              {statusDot}
              {currentStatusLabel}
              <HugeiconsIcon icon={ArrowDown01Icon} size={16} />
            </button>
          ) : (
            <Tooltip title={statusLockReason} placement="top">
              <span className={`${statusPillClassName} cursor-not-allowed`}>
                {statusDot}
                {currentStatusLabel}
                <HugeiconsIcon icon={SquareLock02Icon} size={16} className="opacity-60" />
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <Popover
        open={Boolean(actionsAnchorEl)}
        anchorEl={actionsAnchorEl}
        onClose={closeActionsMenu}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
        className="mt-[8px] w-[300px] p-[8px] !rounded-[16px] shadow-elevation-2"
      >
        <div role="menu" aria-label="Reservation actions">
          {actionGroups.map((group, groupIndex) => (
            <div
              key={group.title}
              className={groupIndex > 0 ? 'border-t border-gray-200 mt-[6px] pt-[6px]' : ''}
            >
              <div className="px-[14px] pt-[8px] pb-[4px] text-[12px] font-gilroy-medium text-neutral-300">
                {group.title}
              </div>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  disabled={!!item.disabledReason}
                  onClick={() => {
                    closeActionsMenu();
                    item.onClick();
                  }}
                  className={menuItemClassName}
                >
                  <HugeiconsIcon icon={item.icon} size={18} className="shrink-0 opacity-70" />
                  <span>{item.label}</span>
                  {item.disabledReason && (
                    <span className="ml-auto text-[11px] text-right">{item.disabledReason}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      </Popover>

      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl}
        onClose={closeStatusMenu}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
        className="mt-[8px] w-[240px] p-[8px] !rounded-[16px] shadow-elevation-2"
      >
        <div role="menu" aria-label="Reservation status">
          {statusOptions?.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              disabled={option.isDisabled}
              onClick={() => {
                closeStatusMenu();
                onOpenAddCommentModal();
                onStatusChange(option.value);
              }}
              className={menuItemClassName}
            >
              <span
                className={`size-[8px] rounded-full shrink-0 ${getStatusStyle(option.value).dot}`}
              />
              {option.label}
            </button>
          ))}
        </div>
      </Popover>

      <SwitchRoomModal
        config={{ showModal: showSwitchRoomModal }}
        data={{ reservation, switchRoomLoading }}
        eventHandlers={{
          closeHandler: () => onSwitchRoomModalChange(false),
          switchRoomHandler: handleSwitchRoom
        }}
      />

      <RaiseTicketModal
        show={showRaiseTicket}
        onClose={() => setShowRaiseTicket(false)}
        context={{
          reservationRef: reservation.id,
          unitRef: reservation.unitTypeId,
          contactRef: reservation.userId,
          customerEmail: reservation.guestsList?.[0]?.email,
          description: `Reservation ${reservation.id} · ${reservation.primaryGuestName} · ${reservation.propertyName} ${reservation.unitName}`
        }}
      />

      <ReservationSourceChannelModal
        data={{
          reservation,
          showModal: showSourceChannelModal,
          sourceChannelDraft,
          sourceChannelSaving,
          disableSourceChannelFields: isChannelManager
        }}
        eventHandlers={{
          onClose: () => setShowSourceChannelModal(false),
          onDraftChange: setSourceChannelDraft,
          onSave: async () => {
            const saved = await saveAttribution(sourceChannelDraft);
            if (saved) setShowSourceChannelModal(false);
          }
        }}
      />
    </>
  );
};
